import './docket-contact-soon.scss';

import { PriorityContactDto } from '@act/shared/data-transfer-objects';
import { useEffect, useMemo, useState } from 'react';
import {
  docketActions,
  docketSliceSelectors,
  selectTabPreferences,
} from 'redux/slices/docketSlice';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import { useDocketSelectors } from 'redux/utils/selectors/docket-selectors';
import ScrollableTable, {
  DEFAULT_ROWS_PER_PAGE_OPTIONS as rowsPerPageOptions,
} from './ScrollableTable';
import { HeaderCellConfig } from 'toolkit/tables/types/header.types';
import { MenuItemConfig } from 'toolkit/tables/types/menu.types';
import {
  PaginationBounds,
  SortParameters,
} from 'toolkit/tables/types/table.types';
import { useAppNavigation } from 'react-components/app/routing/routes';
import useInteractionsDialog from '../../dialog-forms/hooks/use-interactions-dialog';
import ContactSoonTableRow from './contact-soon-table-row';
import Typography from '@mui/material/Typography';
import { DateTime } from 'luxon';
import {
  FlexContainer,
  DocketMobileCard,
  DocketMobilePagination,
} from '@act/storybook';
import { useMobile } from '@act/utilities/mobile-context';
import { Menu, MenuItem } from '@mui/material';

export type ContactSoonColumnKeys =
  | 'member'
  | 'status'
  | 'outreachPhase'
  | 'last-event'
  | 'priority'
  | 'ellipsis-menu';

const headerCellConfig: HeaderCellConfig<ContactSoonColumnKeys>[] = [
  { key: 'member', label: 'Member' },
  { key: 'status', label: 'Status' },
  { key: 'outreachPhase', label: 'Phase' },
  { key: 'last-event', label: 'Last event' },
  { key: 'priority', label: 'Priority', width: 110 },
  { key: 'ellipsis-menu', width: 56 },
];

const CACHED_TAB_KEY: string = 'contact-soon';

const priorityLevelToLabel = {
  high: '≤ 3 days',
  medium: '4-9 days',
  low: '10-15 days',
};

// Helper function to get phase label
const getPhaseLabel = (phase: string) => {
  if (phase?.length > 1) {
    return phase;
  } else if (phase?.length === 1) {
    return 'Phase ' + phase;
  }
  return '';
};

const DocketContactSoon = ({ guideId }: { guideId: string }) => {
  const { isMobile } = useMobile();
  const priorityContactList =
    useDocketSelectors.selectPriorityContactsByGuide(guideId);

  const dispatch = useAppDispatch();
  const isLoading =
    useDocketSelectors.priorityContactsLoadingState() === 'loading';

  // Mobile menu state
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [selectedMobileItem, setSelectedMobileItem] =
    useState<PriorityContactDto | null>(null);

  const cachedBounds =
    useAppSelector(selectTabPreferences)?.tabBounds[CACHED_TAB_KEY];
  const [bounds, setBounds] = useState<PaginationBounds>(() => {
    if (cachedBounds) return cachedBounds;
    return {
      pageSize: rowsPerPageOptions[0],
      pageStart: 0,
    };
  });

  const [sortParams, setSortParams] = useState<
    SortParameters<ContactSoonColumnKeys>
  >(
    () =>
      ({
        pageSize: bounds.pageSize,
        pageStart: bounds.pageStart,
      } as SortParameters<ContactSoonColumnKeys>)
  );
  const [processedData, setProcessedData] = useState<PriorityContactDto[]>([]);

  useEffect(() => {
    void dispatch(
      docketActions.setDocketTabBounds({ tab: CACHED_TAB_KEY, bounds })
    );
  }, [bounds]);

  // Set page size to 10 when switching to mobile
  useEffect(() => {
    if (isMobile && bounds.pageSize !== 10) {
      setBounds(prevBounds => ({
        ...prevBounds,
        pageSize: 10,
        pageStart: 0,
      }));
      setSortParams(prevParams => ({
        ...prevParams,
        pageSize: 10,
        pageStart: 0,
      }));
    }
  }, [isMobile, bounds.pageSize]);

  const { toMemberPage } = useAppNavigation();
  const interactionsDialogHelper = useInteractionsDialog();
  const memberMap = membersAppSelectors.useSelectMembersMap();
  const menuItemConfig: MenuItemConfig<PriorityContactDto>[] = [
    {
      display: 'View member page',
      callback: data => toMemberPage(data.guideId, data.memberId),
      ddTrackingName: 'Outreach-goToMember',
    },
    {
      display: 'Schedule event',
      callback: data =>
        interactionsDialogHelper.createScheduledEvent({
          user: memberMap[data.memberId],
        }),
      ddTrackingName: 'Outreach-scheduleEvent',
    },
  ];

  // Mobile menu handlers
  const handleOpenMobileMenu = (
    event: React.MouseEvent<HTMLElement>,
    item: PriorityContactDto
  ) => {
    setMobileMenuAnchor(event.currentTarget);
    setSelectedMobileItem(item);
  };

  const handleCloseMobileMenu = () => {
    setMobileMenuAnchor(null);
    setSelectedMobileItem(null);
  };

  const handleMobileMenuItemClick = async (callback: () => void) => {
    await callback();
    handleCloseMobileMenu();
  };

  // Get visible menu items for selected mobile item
  const visibleMenuItems = useMemo(() => {
    if (!selectedMobileItem) return [];
    return menuItemConfig;
  }, [selectedMobileItem, menuItemConfig]);

  // Format the last updated date to be like:
  // Aug 5, 2024 12:32 PM CST
  const priorityContactUpdatedAt = DateTime.fromMillis(
    docketSliceSelectors.useSelectPriorityContactLastUpdatedAt()
  ).toFormat('MMM d, yyyy h:mm a ZZZZ');

  useEffect(() => {
    if (!sortParams) return;
    //paginate
    const startIndex = sortParams.pageStart * sortParams.pageSize;
    const newData = priorityContactList.slice(
      startIndex,
      startIndex + sortParams.pageSize
    );
    setProcessedData(newData);
  }, [priorityContactList, sortParams]);

  return (
    <FlexContainer
      direction="column"
      padding={isMobile ? '16px' : '16px 16px 0'}
      height={isMobile ? 'auto' : '100%'}
      width="100%"
      sx={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        overflowY: 'visible',
      }}
    >
      {!!processedData.length && (
        <div className="last-updated" style={{ marginBottom: '8px' }}>
          <Typography component="p" variant="body2">
            Last updated:
            {priorityContactUpdatedAt}
          </Typography>
        </div>
      )}

      {isMobile ? (
        <>
          <FlexContainer
            direction="column"
            gap={2}
            width="100%"
            padding="16px 0 0 0"
            sx={{
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            {processedData.map(item => {
              const member = memberMap[item.memberId];
              const lastEventString = member?.lastEventAt
                ? DateTime.fromMillis(member.lastEventAt).toFormat(
                    'LLL dd, yyyy'
                  )
                : '––';
              const phase = getPhaseLabel(
                member?.standardMemberAttributes?.outreachPhase
              );
              const priorityLabel = priorityLevelToLabel[item.priorityLevel];
              const topic =
                phase && priorityLabel
                  ? `${phase} - ${priorityLabel}`
                  : phase || priorityLabel || 'Contact Soon';

              const handleGotoAction = () => {
                toMemberPage(item.guideId, item.memberId);
              };

              return (
                <DocketMobileCard
                  key={item.memberId}
                  member={member}
                  topic={topic}
                  date={lastEventString}
                  time=""
                  gotoAction={handleGotoAction}
                  openMenu={e => handleOpenMobileMenu(e, item)}
                />
              );
            })}
            <DocketMobilePagination
              page={bounds.pageStart}
              pageSize={bounds.pageSize}
              totalItems={priorityContactList.length}
              onPageChange={(newPage: number) => {
                setBounds(prevBounds => ({
                  ...prevBounds,
                  pageStart: newPage,
                }));
                setSortParams(prevParams => ({
                  ...prevParams,
                  pageStart: newPage,
                }));
              }}
            />
          </FlexContainer>
          <Menu
            anchorEl={mobileMenuAnchor}
            open={Boolean(mobileMenuAnchor)}
            onClose={handleCloseMobileMenu}
          >
            {visibleMenuItems.map((item, index) => (
              <MenuItem
                key={index}
                onClick={() =>
                  handleMobileMenuItemClick(() =>
                    item.callback(selectedMobileItem)
                  )
                }
              >
                {item.display}
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : (
        <ScrollableTable
          data={processedData}
          dataRowElement={ContactSoonTableRow}
          headerCells={headerCellConfig}
          menuItems={menuItemConfig}
          bounds={bounds}
          totalRecords={priorityContactList.length}
          dispatchAction={setSortParams}
          setBounds={setBounds}
          rowKeyGenerator={entry => entry.memberId}
          isLoading={isLoading}
        />
      )}
    </FlexContainer>
  );
};

export default DocketContactSoon;
